import type {
  ChatAnswerResponse,
  ChatMessage,
  FeedbackPayload,
  LinkClickPayload,
  ProductContextItem,
} from "../types/api";

export async function chatOnce(
  apiBaseUrl: string,
  messages: ChatMessage[],
  limit = 5,
): Promise<ChatAnswerResponse> {
  const response = await fetch(`${apiBaseUrl}/chat/llm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, limit }),
  });
  if (!response.ok) {
    throw new Error(`Chat request failed with ${response.status}`);
  }
  return response.json();
}

export async function streamChat(
  apiBaseUrl: string,
  messages: ChatMessage[],
  onDelta: (delta: string) => void,
  onMeta: (payload: ChatAnswerResponse) => void,
  limit = 5,
  productContext?: ProductContextItem[],
): Promise<void> {
  const body: Record<string, unknown> = { messages, limit };
  if (productContext && productContext.length > 0) {
    body.product_context = productContext;
  }
  const response = await fetch(`${apiBaseUrl}/chat/llm/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok || !response.body) {
    throw new Error(`Streaming chat failed with ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const line = block.split("\n").find((item) => item.startsWith("data: "));
      if (line) {
        const event = JSON.parse(line.slice(6));
        if (event.type === "delta") {
          onDelta(event.delta);
        } else if (event.type === "meta") {
          onMeta(event.payload);
        }
      }
      boundary = buffer.indexOf("\n\n");
    }
  }
}

export async function submitFeedback(
  apiBaseUrl: string,
  payload: FeedbackPayload,
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Feedback request failed with ${response.status}`);
  }
}

export async function trackLinkClick(
  apiBaseUrl: string,
  payload: LinkClickPayload,
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/feedback/link-click`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Link click tracking failed with ${response.status}`);
  }
}
